
import { ModalContent } from '../App';
import { FalloutActionType, FalloutCharacter, FalloutCharacterStatus, FalloutPlayer, GameData, MirrorscapeImage, OpenFireRequest, PromptTakeAction, Quaternion, RequestMoveCharacter, Vector3 } from '../Data/Response';
import BrawlSequenceView from './BrawlSequenceView';
import EquippedWeaponView from './EquippedWeaponView';
import styles from './GameCharacterView.module.scss';
import GetMovingActionView from './GetMovingActionView';
import MeterView from './MeterView';
import MoveCharacterModalView from './MoveCharacterModalView';
import MovementOrdersModalView from './MovementOrdersModalView';
import OpenFireSequenceView from './OpenFireSequenceView';
import RotateCharacterModalView from './RotateCharacterModalView';

interface GameCharacterViewArgs {
    character: FalloutCharacter;
    isYou: boolean;
    setModalContent: React.Dispatch<React.SetStateAction<ModalContent>>
    game: GameData;
    player: FalloutPlayer;
    startOpenFire: (args: OpenFireRequest) => Promise<void>;
    startBrawl: (characterId: string, weaponId: string, targetId: string) => Promise<void>;
    actionPrompt: PromptTakeAction;
    submitGetMoving: (characterId: string, movementOrderCharacterIds: string[]) => void;
    submitBackOff: (characterId: string) => void;
    submitPatchUp: (characterId: string) => void;
    submitDevForcePosition: (characterId: string, position: Vector3, rotation: Quaternion) => void;
    submitRotateCharacter: (characterId: string, rotation: Quaternion) => void;
}

const GameCharacterView: React.FC<GameCharacterViewArgs> = args => {
    const c = args.character;
    const pc = args.character;
    // const isActiveCharacter = (previousActionInTurn) ? previousActionInTurn.activeCharacterId === c.id : true;
    if (!pc) {
        throw new Error("Failed to find character in army.");
    }
    // const character = pc.data.character;
    // const harm = pc.data['Harm'] ?? 0;
    // const fatigue = pc.data['Fatigue'] ?? 0;

    // const rangedWeapon = pc.getRangedWeapon();
    // const openFireUI = (rangedWeapon && isYou)
    //     ? 
    //     : null;
    const availableActions = args.actionPrompt?.charactersAvailableActions[c.id] ?? [];
    const isGetMovingDisabled = availableActions.find(a => a.type === "getMoving") === undefined;
    const isBackOffDisabled = availableActions.find(a => a.type === "backOff") === undefined;
    const isPatchUpDisabled = availableActions.find(a => a.type === "patchUp") === undefined;
    return <li className={`${styles.character} ${c.state.status !== "active" ? styles.removed : ""}`}>
        <CharacterHeaderView
            name={c.name}
            id={c.id}
            className={c.className}
            avatar={c.avatar}
            status={c.state.status}
        />
        {c.engagedEnemyIds.length < 1 ? <p>Not Engaged</p> : <p>Engaged with {c.engagedEnemyIds.join(", ")}</p>}
        <button
            onClick={e => {
                args.setModalContent({
                    element: <MoveCharacterModalView
                        character={c}
                        maxStandardMovement={100}
                        submitMoveCharacter={function (request: RequestMoveCharacter): void {
                            args.submitDevForcePosition(c.id, request.Path[request.Path.length - 1], request.Rotation)
                        }}
                    />,
                    canClose: true
                });
            }}
        >Dev Force Position Test</button>
        <button
            onClick={e => {
                args.setModalContent({
                    element: <RotateCharacterModalView
                        character={c}
                        submitRotateCharacter={args.submitRotateCharacter}
                    />,
                    canClose: true
                });
            }}
        >Rotate Character</button>
        <table>
            <tr>
                <th>Harm</th>
                <th>Fatigue</th>
                <th>Injuries</th>
                <th>Endurance</th>
                <th>Luck</th>
            </tr>
            <tr>
                <td><MeterView current={c.state.harm} max={3} /></td>
                <td><MeterView current={c.state.fatigue} max={2} /></td>
                <td><MeterView current={c.state.injuries} max={c.stats.health} /></td>
                <td>{c.stats.endurance}</td>
                <td>{c.stats.luck}</td>
            </tr>
        </table>
        <div>
            <h4>Actions</h4>
            <ul className={styles.actionList}>
                {c.weapons.map(w => <EquippedWeaponView
                    isYou={args.isYou}
                    weapon={w}
                    character={c}
                    openFire={() => args.setModalContent({
                        element: <OpenFireSequenceView
                            game={args.game}
                            player={args.player}
                            actor={pc}
                            equippedItem={w}
                            startOpenFire={args.startOpenFire}
                        />,
                        canClose: true
                    })}
                    brawl={() => args.setModalContent({
                        element: <BrawlSequenceView
                            targets={args.game.falloutPlayers.flatMap(p => {
                                if (p.userId == args.player.userId)
                                    return [];
                                return p.crew?.characters ?? [];
                            }).filter(t => args.character.engagedEnemyIds.includes(t.id))}
                            brawlHandler={targetId => {
                                args.startBrawl(args.character.id, w.id, targetId)
                            }}
                        />,
                        canClose: true
                    })}
                    actionPrompt={args.actionPrompt}
                />)}
                {<GetMovingActionView
                    isYou={args.isYou}
                    isDisabled={isGetMovingDisabled}
                    getMoving={() => {
                        console.log("Got moving handler.");
                        args.setModalContent({
                            canClose: true,
                            element: <MovementOrdersModalView
                                characterId={c.id}
                                otherCharacters={(args.player.crew?.characters ?? []).filter(char =>
                                    char.id !== c.id &&
                                    char.state.fatigue < 2 &&
                                    char.state.status === "active"
                                )}
                                submitGetMoving={args.submitGetMoving} />
                        });
                    }}
                    name="Get Moving"
                />}
                {<GetMovingActionView
                    isYou={args.isYou}
                    isDisabled={isBackOffDisabled}
                    getMoving={() => {
                        args.setModalContent({
                            canClose: true,
                            element: <div>
                                <h3>Confirm</h3>
                                <p>Are you sure you want to take Back Off action with {c.name}?</p>
                                <button
                                    onClick={e => {
                                        args.submitBackOff(c.id)
                                    }}
                                >Confirm Back Off</button>
                            </div>
                        });
                    }}
                    name="Back Off"
                />}
                {<GetMovingActionView
                    isYou={args.isYou}
                    isDisabled={isPatchUpDisabled}
                    getMoving={() => {
                        args.setModalContent({
                            canClose: true,
                            element: <div>
                                <h3>Confirm</h3>
                                <p>Are you sure you want to Patch Up {c.name}?</p>
                                <button
                                    onClick={e => {
                                        args.submitPatchUp(c.id)
                                    }}
                                >Confirm Patch Up</button>
                            </div>
                        });
                    }}
                    name="Patch Up"
                />}
            </ul>
        </div>
    </li>
}

interface CharacterHeaderViewArgs {
    avatar: MirrorscapeImage;
    name: string;
    id: string;
    className: string;
    status: FalloutCharacterStatus;
}

const CharacterHeaderView: React.FC<CharacterHeaderViewArgs> = args => {


    return <div className={styles.characterHeader}>
        <div className={styles.avatar}><img src={args.avatar.smallUrl} /></div>
        <div className={styles.info}>
            <h2 className={styles.name}>{args.name}</h2>
            <h3 className={styles.id}>{args.id}</h3>
            <h3 className={styles.class}>{args.className}</h3>
            <h3 className={styles.status}>{GetCharacterStatusString(args.status)}</h3>
        </div>
    </div>
}

const GetCharacterStatusString = (status: FalloutCharacterStatus): string => {
    switch (status) {
        case "active": return "Active";
        case "fledBattlefield": return "FLED";
        case "incapacitated": return "INCAPACITATED";
        case "pendingIncapacitated": return "PENDING INCAPACITATED";
    }
}




export default GameCharacterView;